/**
 * Delay non-critical third-party scripts until interaction / idle.
 * Must run synchronously in <head> before {{ content_for_header }}.
 *
 * Homepage TagEmbed/Tagbox (Instagram feed): hold until the feed section is
 * near the viewport (IntersectionObserver). Do not strip Shopify asyncLoad —
 * intercept the scripts it injects instead.
 *
 * Never remove TagEmbed functionality; only change WHEN it loads.
 */
(function () {
  if (window.__eckoThirdPartyGuard) return;
  window.__eckoThirdPartyGuard = true;

  var isProduct = window.__eckoPageType === 'product';
  var isIndex = window.__eckoPageType === 'index';

  var deferPatterns = [
    /connect\.facebook\.net/i,
    /facebook\.net\/.*fbevents/i,
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /googletagservices\.com/i,
    /googleadservices\.com/i,
    /doubleclick\.net/i,
    /analytics\.tiktok\.com/i,
    /tiktok\.com\/i18n\/pixel/i,
    /hotjar\.com/i,
    /clarity\.ms/i,
    /chatway\.app/i,
    /cdn\.chatway/i,
    /snap\.licdn\.com/i,
    /ads-twitter\.com/i,
    /static\.ads-twitter\.com/i,
    /bat\.bing\.com/i,
  ];

  if (!isProduct) {
    deferPatterns.push(/app\.kiwisizing\.com/i, /trustshop/i, /releasit/i);
  }

  var tagembedSrcPatterns = [/tagembed\.com/i, /tagbox\.com/i, /cloud\.tagbox/i];
  var tagembedAssetPatterns = [
    /tagembed\.com/i,
    /tagbox\.com/i,
    /cloud\.tagbox/i,
    /instagram-feed\.css/i,
    /WebSocialFonts/i,
  ];

  var activated = false;
  var blocked = [];
  var tagembedBlocked = [];
  var tagembedStyleLinks = [];
  var tagembedReady = false;
  var keptOrigins = {};

  function isTagembedSrc(src) {
    return !!src && tagembedSrcPatterns.some(function (re) {
      return re.test(src);
    });
  }

  function isTagembedAsset(href) {
    return !!href && tagembedAssetPatterns.some(function (re) {
      return re.test(href);
    });
  }

  function shouldDefer(src) {
    if (!src) return false;
    if (isIndex && isTagembedSrc(src)) return false; /* handled separately */
    return deferPatterns.some(function (re) {
      return re.test(src);
    });
  }

  function shouldDeferText(text) {
    if (!text) return false;
    if (text.indexOf('asyncLoad') !== -1) return false;
    if (isIndex && /tagembed\.com|tagbox\.com/i.test(text) && !/facebook|gtag|gtm|kiwi/i.test(text)) {
      return false;
    }
    return deferPatterns.some(function (re) {
      return re.test(text);
    });
  }

  function originKey(href) {
    try {
      return new URL(href, window.location.href).origin;
    } catch (e) {
      return href;
    }
  }

  function keepEarlyHint(href) {
    if (!href) return false;
    return /cdn\.shopify\.com|fonts\.shopifycdn\.com/i.test(href);
  }

  function interceptLink(node) {
    if (!node || node.tagName !== 'LINK' || !node.rel) return;
    var rel = String(node.rel).toLowerCase();
    var href = node.href || node.getAttribute('href') || '';

    if (rel === 'stylesheet' && isIndex && isTagembedAsset(href)) {
      if (node.media !== 'print') {
        node.media = 'print';
        node.dataset.eckoTagembedCss = '1';
      }
      if (tagembedStyleLinks.indexOf(node) === -1) tagembedStyleLinks.push(node);
      return;
    }

    if (rel === 'preload' && isIndex && /WebSocialFonts|tagbox|tagembed/i.test(href)) {
      try {
        node.parentNode && node.parentNode.removeChild(node);
      } catch (e) {}
      return;
    }

    if (rel !== 'preconnect' && rel !== 'dns-prefetch') return;

    if (keepEarlyHint(href)) {
      var key = originKey(href) + '|' + rel;
      if (keptOrigins[key]) {
        try {
          node.parentNode && node.parentNode.removeChild(node);
        } catch (e) {}
        return;
      }
      keptOrigins[key] = true;
      return;
    }

    try {
      node.parentNode && node.parentNode.removeChild(node);
    } catch (e) {}
  }

  function injectScript(entry) {
    var script = document.createElement('script');
    script.async = true;
    if (entry.src) script.src = entry.src;
    else if (entry.text) script.text = entry.text;
    (entry.parent || document.head || document.body).appendChild(script);
  }

  function activatePixels() {
    if (activated) return;
    activated = true;
    blocked.forEach(injectScript);
    blocked = [];
    document.documentElement.classList.add('ecko-thirdparty-ready');
  }

  function activateTagembed() {
    if (tagembedReady) return;
    tagembedReady = true;

    tagembedStyleLinks.forEach(function (link) {
      try {
        link.media = 'all';
        link.removeAttribute('data-ecko-tagembed-css');
      } catch (e) {}
    });
    tagembedStyleLinks = [];

    tagembedBlocked.forEach(injectScript);
    tagembedBlocked = [];
    document.documentElement.classList.add('ecko-tagembed-ready');
  }

  function intercept(node) {
    if (!node) return;
    if (node.tagName === 'LINK') {
      interceptLink(node);
      return;
    }
    if (node.tagName !== 'SCRIPT') return;

    var src = node.src || '';
    var text = node.textContent || '';

    if (isIndex && isTagembedSrc(src) && !tagembedReady) {
      tagembedBlocked.push({ src: src, text: '', parent: node.parentNode });
      try {
        node.parentNode && node.parentNode.removeChild(node);
      } catch (e) {}
      return;
    }

    if (activated) return;
    if (!shouldDefer(src) && !shouldDeferText(text)) return;

    blocked.push({
      src: src,
      text: src ? '' : text,
      parent: node.parentNode,
    });
    try {
      node.parentNode && node.parentNode.removeChild(node);
    } catch (e) {}
  }

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(intercept);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  function collectTagembedCss() {
    if (!isIndex) return;
    var links = document.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var href = link.href || link.getAttribute('href') || '';
      if (!isTagembedAsset(href)) continue;
      link.media = 'print';
      link.dataset.eckoTagembedCss = '1';
      if (tagembedStyleLinks.indexOf(link) === -1) tagembedStyleLinks.push(link);
    }
  }

  function watchTagembed() {
    if (!isIndex || tagembedReady) return;
    collectTagembedCss();

    var target =
      document.querySelector('.tagembed-socialwall') ||
      document.querySelector('tagembed-widget') ||
      document.querySelector('.ecko-tagembed-slot');

    if (!target) {
      /* No widget on page — nothing to do */
      return;
    }

    var root = target.closest('.shopify-section') || target;

    function near(el) {
      var r = el.getBoundingClientRect();
      return r.top < window.innerHeight + 400 && r.bottom > -100;
    }

    if (near(root)) {
      activateTagembed();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      activateTagembed();
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            activateTagembed();
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: '400px 0px', threshold: 0 }
    );
    io.observe(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchTagembed);
  } else {
    watchTagembed();
  }

  /* Pixels / kiwi / etc. — interaction or idle after load (not TagEmbed) */
  window.addEventListener(
    'scroll',
    function () {
      if (window.scrollY > 120) activatePixels();
    },
    { passive: true }
  );
  window.addEventListener('pointerdown', activatePixels, { once: true });
  window.addEventListener('touchstart', activatePixels, { once: true, passive: true });
  window.addEventListener('keydown', activatePixels, { once: true });

  window.addEventListener('load', function () {
    window.setTimeout(activatePixels, isIndex ? 3000 : 1500);
    /* Safety: if IO never fires (odd layout), still load feed */
    if (isIndex) {
      window.setTimeout(function () {
        if (!tagembedReady) activateTagembed();
      }, 12000);
    }
  });
})();
